import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircleIcon,
  CalendarDaysIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  VideoCameraIcon,
  BuildingOfficeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

const API_BASE = process.env.REACT_APP_API_URL || '';

function groupSlotsByDate(slots, timezone) {
  const groups = {};
  for (const slot of slots) {
    const dateKey = new Date(slot.startTime).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timezone,
    });
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(slot);
  }
  return groups;
}

function formatTime(isoString, timezone) {
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
}

export default function ScheduleSlotPicker() {
  const { bookingToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);
  const [alreadyBooked, setAlreadyBooked] = useState(null);

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchSlots = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/scheduling/book/${bookingToken}`);

      if (res.data.alreadyBooked) {
        setAlreadyBooked(res.data.bookedSlot);
        setData({
          meetingType: res.data.meetingType,
          inPersonAddress: res.data.inPersonAddress,
        });
        setLoading(false);
        return;
      }

      setData(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 410) {
        setError('expired');
      } else if (err.response?.status === 404) {
        setError('not_found');
      } else {
        setError('generic');
      }
    } finally {
      setLoading(false);
    }
  }, [bookingToken]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  useEffect(() => {
    if (!data || booked) return;
    const interval = setInterval(fetchSlots, 15000);
    return () => clearInterval(interval);
  }, [data, booked, fetchSlots]);

  const handleBook = async () => {
    if (!selectedSlotId) return;
    setBooking(true);
    try {
      const res = await axios.post(`${API_BASE}/api/scheduling/book/${bookingToken}`, {
        slotId: selectedSlotId,
      });
      setBooked(res.data.booking);
    } catch (err) {
      if (err.response?.status === 409) {
        setSelectedSlotId(null);
        fetchSlots();
        alert(err.response.data.message || 'That slot was just taken. Please pick another.');
      } else {
        alert('Something went wrong. Please try again.');
      }
    } finally {
      setBooking(false);
    }
  };

  const displayTimezone = data?.timezone || browserTimezone;

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <span className="inline-block w-5 h-5 border-2 border-slate-200 border-t-[#007BFF] rounded-full animate-spin" />
          <span className="text-sm">Loading available times…</span>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-md w-full p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            {error === 'expired'
              ? 'Link Expired'
              : error === 'not_found'
                ? 'Invalid Link'
                : 'Something Went Wrong'}
          </h2>
          <p className="text-sm text-slate-500">
            {error === 'expired'
              ? 'This booking link has expired. Please contact the recruiter for a new link.'
              : error === 'not_found'
                ? 'This booking link is invalid. Please check the link from your email.'
                : 'We couldn\'t load the available slots. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  // Already booked
  if (alreadyBooked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-md w-full p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Already Booked</h2>
          <p className="text-sm text-slate-500 mb-4">You've already scheduled your interview.</p>
          {alreadyBooked.startTime && (
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700">
              <p className="font-medium">
                {new Date(alreadyBooked.startTime).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: displayTimezone,
                })}
              </p>
              <p className="text-slate-500 mt-1">
                {formatTime(alreadyBooked.startTime, displayTimezone)} — {formatTime(alreadyBooked.endTime, displayTimezone)}
              </p>
              {alreadyBooked.meetingLink && (
                <a
                  href={alreadyBooked.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 text-[#007BFF] hover:underline text-sm font-medium"
                >
                  Join Meeting
                </a>
              )}
              {/* Show meeting type info */}
              {data?.meetingType === 'in_person' && data?.inPersonAddress && (
                <div className="mt-3 flex items-start gap-2 text-left bg-blue-50 rounded-lg px-3 py-2">
                  <MapPinIcon className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-blue-800">In-Person Interview</p>
                    <p className="text-xs text-blue-600 whitespace-pre-line mt-0.5">{data.inPersonAddress}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Booking confirmed
  if (booked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-md w-full p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Interview Booked!</h2>
          <p className="text-sm text-slate-500 mb-5">You'll receive a confirmation email with calendar invite shortly.</p>
          <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <CalendarDaysIcon className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-slate-700 font-medium">{booked.date}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ClockIcon className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-slate-700">{booked.time}</span>
            </div>
            {booked.meetingLink && (
              <a
                href={booked.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 px-4 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-lg hover:bg-blue-600 transition-colors"
              >
                Join Meeting
              </a>
            )}
            {/* Interview mode indicator */}
            {booked.meetingType === 'in_person' ? (
              <div className="flex items-start gap-2 mt-2">
                <MapPinIcon className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                <div>
                  <span className="text-sm text-slate-700">In-person interview</span>
                  {booked.inPersonAddress && (
                    <p className="text-xs text-slate-500 whitespace-pre-line mt-0.5">{booked.inPersonAddress}</p>
                  )}
                </div>
              </div>
            ) : booked.meetingLink ? null : (
              <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                <VideoCameraIcon className="h-4 w-4 text-slate-400 shrink-0" />
                <span>Virtual interview — link will be emailed</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Slot picker
  const grouped = groupSlotsByDate(data.slots, displayTimezone);
  const noSlots = data.slots.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#007BFF] flex items-center justify-center">
              <span className="text-white font-bold text-sm">T</span>
            </div>
            <span className="text-sm font-medium text-slate-500">TalentCrux</span>
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mt-3">Pick a time for your interview</h1>
          {data.jobTitle && (
            <p className="text-sm text-slate-500 mt-1">
              {data.jobTitle}{data.companyName ? ` at ${data.companyName}` : ''}
            </p>
          )}
          {data.note && (
            <div className="mt-3 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <p className="text-xs text-amber-700">{data.note}</p>
            </div>
          )}
          {/* Interview Mode Info */}
          <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              {data.meetingType === 'in_person' ? (
                <>
                  <BuildingOfficeIcon className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-sm font-semibold text-blue-900">In-Person Interview</span>
                </>
              ) : (
                <>
                  <VideoCameraIcon className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-sm font-semibold text-blue-900">Virtual Interview (Video Call)</span>
                </>
              )}
            </div>

            {data.meetingType === 'in_person' && data.inPersonAddress && (
              <div className="flex items-start gap-2 mt-2">
                <MapPinIcon className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 whitespace-pre-line">{data.inPersonAddress}</p>
              </div>
            )}

            <p className="text-xs text-slate-500 mt-2">
              {data.meetingType === 'in_person'
                ? 'Please carry a valid photo ID and arrive 10–15 minutes early.'
                : 'A video call link will be shared once you confirm your slot.'}
            </p>
          </div>
        </div>
      </div>

      {/* Slots */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {noSlots ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">No available slots</p>
            <p className="text-xs text-slate-400 mt-1">
              All time slots have been booked. Please contact the recruiter for alternative times.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {Object.entries(grouped).map(([dateLabel, slots]) => (
              <div key={dateLabel}>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">{dateLabel}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {slots.map((slot) => {
                    const isSelected = selectedSlotId === slot.id;
                    return (
                      <button
                        key={slot.id}
                        onClick={() => setSelectedSlotId(isSelected ? null : slot.id)}
                        className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-all ${
                          isSelected
                            ? 'border-[#007BFF] bg-[#007BFF] text-white shadow-md'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-[#007BFF] hover:bg-blue-50/50'
                        }`}
                      >
                        {formatTime(slot.startTime, displayTimezone)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Confirm bar */}
        {selectedSlotId && (
          <div className="sticky bottom-0 mt-6 bg-white rounded-xl border border-slate-200 shadow-lg p-4 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              <span className="font-medium text-slate-900">
                {(() => {
                  const slot = data.slots.find((s) => s.id === selectedSlotId);
                  if (!slot) return '';
                  const date = new Date(slot.startTime).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    timeZone: displayTimezone,
                  });
                  return `${date} at ${formatTime(slot.startTime, displayTimezone)}`;
                })()}
              </span>
              <span className="text-slate-400 ml-1">({data.slotDuration} min)</span>
            </div>
            <button
              onClick={handleBook}
              disabled={booking}
              className="px-5 py-2.5 text-sm font-medium text-white bg-[#007BFF] rounded-lg hover:bg-blue-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all shadow-sm inline-flex items-center gap-2"
            >
              {booking ? (
                <>
                  <span className="inline-block w-3.5 h-3.5 border-2 border-white/80 border-t-white rounded-full animate-spin" />
                  Booking…
                </>
              ) : (
                'Confirm Booking'
              )}
            </button>
          </div>
        )}

        {/* Timezone note */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Times shown in {displayTimezone}
        </p>
      </div>
    </div>
  );
}
